import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import "./BoardofDirectors.css";
import useInView from "../../hooks/useInView";
import { staggerContainer, fadeInUp } from "../../utils/animation";
import AKINWALE from "../../assets/images/directors/AKINWALE.png";
import TAYO from "../../assets/images/directors/TAYO.png";
import Tokunbo from "../../assets/images/directors/Tokunbo.png";
import Toyin from "../../assets/images/directors/Toyin.png";

const directors = [
  {
    id: 1,
    name: "Omotayo Gabriel Moronkeji Ajani",
    title: "Chairman",
    image: TAYO,
    bio: "Omotayo holds a B.Sc. in Accounting from the University of Ilorin and an Executive MBA from Lagos Business School, Pan Atlantic University, and Bangor University, UK.",
    fullBio:
      "Omotayo holds a B.Sc. in Accounting from the University of Ilorin and an Executive MBA from Lagos Business School, Pan Atlantic University, and Bangor University, UK. He is a full member of the Nigerian Institute of Management and other professional bodies. With over 23 years of banking experience, he transitioned into insurance as a Director on the board of KBL Healthcare Limited. He currently serves as the Chairman of Thrive Insurance Brokers Limited.",
    expertise: [
      "Corporate Governance",
      "Strategic Planning",
      "Risk Management",
      "Business Development",
    ],
    
  },
  {
    id: 2,
    name: "Akinwale Adegboyega Adedoyin",
    title: "MD/CEO",
    image: AKINWALE,
    bio: "Akinwale is the CEO of Thrive Insurance Brokers Limited. He holds a B.Sc. in Accounting and an MBA in Finance & Accounting.",
    fullBio:
      "Akinwale is the CEO of Thrive Insurance Brokers Limited. He holds a B.Sc. in Accounting and an MBA in Finance & Accounting. He is a Fellow of several professional institutes including ICAN, ICSA, and CIBN. With over 25 years of experience spanning insurance, consulting, and banking, he has worked with organizations such as Gateway Radio, Guinea Insurance Plc, Excel Insurance Brokers, Coral International Insurance Company Limited, among others.",
    expertise: [
      "Financial Strategy",
      "Risk Management",
      "Investment Banking",
      "Institutional Finance",
    ],
    
  },
  {
    id: 3,
    name: "Tokunbo Abiola Adeleke",
    title: "Executive Director",
    image: Tokunbo,
    bio: "Tokunbo earned her B.Sc. in Geology from the Federal University of Technology Minna. She has over 8 years of experience in insurance and financial institutions.",
    fullBio:
      "Tokunbo earned her B.Sc. in Geology from the Federal University of Technology Minna. She has over 8 years of experience in insurance and financial institutions. Her career began at Mutual Benefits Insurance Plc, where she worked in business development roles. She is currently an Executive Director at Thrive Insurance Brokers Limited. She is concluding her studies to become a chartered insurer (CII UK).",
    expertise: [
      "Entrepreneurship",
      "Innovation",
      "Growth Strategy",
      "Sustainable Business",
    ],
    
  },
    {
    id: 4,
    name: "Oluwatoyin Adewuyi",
    title: "Executive Director",
    image: Toyin,
    bio: "Oluwatoyin Adewuyi holds a Bachelor of Technology degree in Transport Management from Ladoke Akintola University of Technology, Ogbomosho with specialization on Aviation and Maritime.",
    fullBio:
      "Oluwatoyin Adewuyi holds a Bachelor of Technology degree in Transport Management from Ladoke Akintola University of Technology, Ogbomosho with specialization on Aviation and Maritime. She has Diploma in Foundation on Travel and Tourism (IATA). She also has Master's degree in Transportation Planning and Management from the prestigious University of Lagos (UNILAG). Toyin has worked with different organizations in the Financial Institutions, Transportation and Maritime sectors ranging from Sterling Bank Plc, Bollore Africa Logistics (SDV), Lagos State Ministry of Transportation and Vehicle Licensing and Inspection office. She has taken several training in the areas of Travels, Tourisms, Airfares and Ticketing, IATA/UFTAA. She's also an expert on all GDS portal. Her interest in Transportation Management, Travels and Tourism started from the youthful years. Hence, her decision to choose a related career path as reflected in the educational background and work experience. Oluwatoyin was recently appointed as an Executive Director of Thrive Travels & Tours Limited.",
    expertise: [
      "Entrepreneurship",
      "Innovation",
      "Growth Strategy",
      "Sustainable Business",
    ],

  },
];

const DirectorModal = ({ director, isOpen, onClose }) => {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocusedElement = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const focusCloseButton = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusableElements = dialogRef.current.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!firstElement || !lastElement) {
        event.preventDefault();
        return;
      }

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusCloseButton);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      previouslyFocusedElement?.focus?.();
    };
  }, [isOpen, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && director && (
        <motion.div
          className="director-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <motion.article
            ref={dialogRef}
            className="director-modal-content"
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 16 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-director-name"
            aria-describedby="modal-director-bio"
          >
            <button
              ref={closeButtonRef}
              className="director-modal-close-btn"
              type="button"
              onClick={onClose}
              aria-label="Close director details"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>

            <div className="director-modal-scroll">
              <div className="director-modal-grid">
                <div className="director-modal-image-section">
                  <div className="director-modal-image-frame">
                    <motion.img
                      key={director.id}
                      src={director.image}
                      alt={director.name.trim()}
                      className="director-modal-image"
                      initial={{ scale: 1.04, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.08, duration: 0.4 }}
                    />
                  </div>
                  {/* <div className="director-modal-image-caption">
                    <span>Leadership</span>
                    <span>{String(director.order).padStart(2, "0")}</span>
                  </div> */}
                </div>

                <div className="director-modal-details">
                  <header className="director-modal-heading">
                    <span className="director-modal-eyebrow">
                      Board of Directors
                    </span>
                    <h2
                      id="modal-director-name"
                      className="director-modal-name"
                    >
                      {director.name.trim()}
                    </h2>
                    <p className="director-modal-title">{director.title}</p>
                  </header>

                  <section className="director-modal-bio-section">
                    <h3 className="director-modal-section-label">Profile</h3>
                    <p
                      id="modal-director-bio"
                      className="director-modal-bio"
                    >
                      {director.fullBio}
                    </p>
                  </section>

                  <section className="director-modal-expertise-section">
                    <h3 className="director-modal-section-label">
                      Areas of expertise
                    </h3>
                    <div className="expertise-tags" aria-label="Expertise">
                      {director.expertise.map((item) => (
                        <span key={item} className="expertise-tag">
                          {item}
                        </span>
                      ))}
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </motion.article>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

const DirectorProfileCard = ({ director, onCardClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="director-profile-card"
      variants={fadeInUp}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.02 }}
      onClick={() => onCardClick(director)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onCardClick(director);
        }
      }}
      role="button"
      tabIndex={0}
      aria-haspopup="dialog"
      aria-label={`View ${director.name.trim()}'s profile`}
    >
      {/* Hexagon Background Accent */}
      <motion.div
        className="hexagon-accent"
        animate={{ rotate: isHovered ? 360 : 0 }}
        transition={{ duration: 1.2, ease: "easeInOut" }}
      />

      {/* Main Image Container with gradient overlay */}
      <div className="profile-image-container">
        <motion.img
          src={director.image}
          alt={director.name}
          className="profile-image"
          whileHover={{ scale: 1.12 }}
          transition={{ duration: 0.4 }}
        />
        <motion.div
          className="profile-gradient-overlay"
          animate={{ opacity: isHovered ? 1 : 0.3 }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Content Section */}
      <motion.div
        className="profile-content"
        animate={{ y: isHovered ? 0 : 8 }}
        transition={{ duration: 0.3 }}
      >
        <h3 className="profile-name">{director.name}</h3>
        <p className="profile-title">{director.title}</p>

        {/* Expand to show bio on hover */}
        <motion.p
          className="profile-bio"
          animate={{
            opacity: isHovered ? 1 : 0,
            height: isHovered ? "auto" : 0,
          }}
          transition={{ duration: 0.3 }}
        >
          {director.bio}
        </motion.p>
      </motion.div>

      {/* Floating accent dots */}
      <motion.div
        className="accent-dot accent-dot-1"
        animate={{ y: isHovered ? -8 : 0 }}
        transition={{ duration: 0.4 }}
      />
      <motion.div
        className="accent-dot accent-dot-2"
        animate={{ y: isHovered ? 8 : 0 }}
        transition={{ duration: 0.4 }}
      />

      {/* Click to view more indicator */}
      <motion.div
        className="click-indicator"
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      >
        Click to view more
      </motion.div>
    </motion.div>
  );
};

const BoardOfDirectors = () => {
  const [ref, inView] = useInView({ threshold: 0.12 });
  const [selectedDirector, setSelectedDirector] = useState(null);

  return (
    <>
      {/* Modal */}
      <DirectorModal
        director={selectedDirector}
        isOpen={!!selectedDirector}
        onClose={() => setSelectedDirector(null)}
      />

      {/* Hero Section */}
      <div className="board board-fresh d-flex flex-column justify-content-center align-items-center p-5 position-relative">
        <motion.div
          className="board-content-wrapper"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <h1 className="fw-bold display-1 red-text">Board Of Directors</h1>
          <p className="board-subtitle">
            Leading Excellence Through Vision & Integrity
          </p>
        </motion.div>

        {/* Animated Background Elements */}
        <motion.div
          className="bg-decoration bg-decoration-1"
          animate={{ x: [0, 20, 0], y: [0, -15, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="bg-decoration bg-decoration-2"
          animate={{ x: [0, -20, 0], y: [0, 15, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Directors Showcase Section */}
      <section className="board-directors-showcase py-5">
        <div className="container">
          <motion.div
            ref={ref}
            className="directors-showcase-grid"
            variants={staggerContainer}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            {directors.map((director) => (
              <DirectorProfileCard
                key={director.id}
                director={director}
                onCardClick={setSelectedDirector}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <motion.section
        className="cta-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container text-center">
          <h2 className="cta-title">Our Leadership Excellence</h2>
          <p className="cta-subtitle">
            Driven by expertise, innovation, and a commitment to transforming
            industries
          </p>
        </div>
      </motion.section>
    </>
  );
};

export default BoardOfDirectors;
